import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../foundations/Icon';
import advCss from '../AdvancedAutoComplete/AdvancedAutoComplete.css';
import inputCss from '../AdvancedAutoComplete/AdvancedAutoCompleteInput.css';
import css from './FilterTextField.css';

const PLACEHOLDER = 'STATIC.FILTERS_SECTION.TEXT_FILTER_PLACEHOLDER';

const FilterTextField = ({
    label,
    value = '',
    operator = '',
    onChange,
    disabled = false,
    removable = false,
    onRemove = () => {},
    containerClass = '',
    translate,
}) => {
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const inputRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const commitChange = useMemo(
        () => debounce(nextValue => onChangeRef.current(nextValue), 300, { trailing: true }),
        []
    );

    useEffect(() => () => commitChange.cancel(), [commitChange]);

    const handleChange = event => {
        setInputValue(event.target.value);
        commitChange(event.target.value);
    };

    const handleRemove = event => {
        event.stopPropagation();
        onRemove();
    };

    return (
        <div className={classNames(advCss.container, containerClass)}>
            <div className={advCss.wrapper}>
                <span className={classNames(advCss.label, advCss.labelFloat, { [css.labelFocused]: focused })}>
                    <Translate id={label} />
                </span>
                <div
                    className={classNames(inputCss.inputWrapper, css.textFieldWrapper, {
                        [inputCss.disabledView]: disabled,
                        [css.focused]: focused,
                    })}
                    onClick={() => inputRef.current?.focus()}
                >
                    {!!inputValue && !!operator && <span className={css.operatorPill}>{operator}</span>}
                    <input
                        ref={inputRef}
                        type="text"
                        autoComplete="off"
                        className={css.input}
                        value={inputValue}
                        placeholder={translate(PLACEHOLDER)}
                        aria-label={translate(label)}
                        disabled={disabled}
                        onChange={handleChange}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                    />
                    {removable && (
                        <Icon
                            type={IconTypes.MINGCUTE}
                            name="close-circle-fill"
                            size={IconSizes.MEDIUM}
                            variant={IconVariants.LIGHT}
                            onClick={handleRemove}
                            disabled={disabled}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

FilterTextField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    operator: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    removable: PropTypes.bool,
    onRemove: PropTypes.func,
    containerClass: PropTypes.string,
    translate: PropTypes.func,
};

export default withLocalize(FilterTextField);
